import { authHeaders, fetchWithAuthRetry, getApiUrl, parseApiError } from './common';

export type NotificationStatus = 'UNREAD' | 'READ' | 'ARCHIVED' | 'SNOOZED';

export type NotificationItem = {
  id: string;
  memberId: string;
  projectId: string;
  type: string;
  status: NotificationStatus;
  title: string;
  body: string;
  url: string;
  entityId: string | null;
  relatedPlanId: string | null;
  createdAt: string;
  updatedAt: string;
  project?: { id: string; name: string; teamId: string } | null;
};

export type NotificationListResponse = {
  data: NotificationItem[];
  pagination: {
    page: number;
    limit: number;
    totalCount: number;
    totalPages: number;
  };
};

type UnreadCountResponse = {
  data: {
    count: number;
  };
};

type NotificationItemResponse = {
  data: NotificationItem;
};

type ReadAllResponse = {
  data: {
    count: number;
  };
};

type ReadTargetResponse = {
  data: {
    count: number;
  };
};

export type NotificationListParams = {
  page?: number;
  limit?: number;
  status?: NotificationStatus;
  type?: string;
  projectId?: string;
};

export const fetchNotifications = async (
  token: string,
  projectId: string,
  params: NotificationListParams = {},
): Promise<NotificationListResponse> => {
  const searchParams = new URLSearchParams();
  if (params.page) searchParams.set('page', String(params.page));
  if (params.limit) searchParams.set('limit', String(params.limit));
  if (params.status) searchParams.set('status', params.status);
  if (params.type) searchParams.set('type', params.type);

  const qs = searchParams.toString();
  const url = `${getApiUrl()}/api/projects/${projectId}/notifications${qs ? `?${qs}` : ''}`;

  const response = await fetchWithAuthRetry(url, {
    cache: 'no-store',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch notifications: ${response.status}`);
  }

  return (await response.json()) as NotificationListResponse;
};

export const fetchUnreadNotificationCount = async (token: string, projectId: string): Promise<number> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/projects/${projectId}/notifications/unread-count`, {
    cache: 'no-store',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch unread count: ${response.status}`);
  }

  const data = (await response.json()) as UnreadCountResponse;
  return data.data.count;
};

export const markNotificationAsRead = async (
  token: string,
  projectId: string,
  id: string,
): Promise<NotificationItem> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/projects/${projectId}/notifications/${id}`, {
    method: 'PATCH',
    headers: authHeaders(token, true),
    body: JSON.stringify({ status: 'READ' }),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to mark notification as read: ${response.status}`);
  }

  const data = (await response.json()) as NotificationItemResponse;
  return data.data;
};

export const markAllNotificationsAsRead = async (token: string, projectId: string): Promise<number> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/projects/${projectId}/notifications/read-all`, {
    method: 'PATCH',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to mark all as read: ${response.status}`);
  }

  const data = (await response.json()) as ReadAllResponse;
  return data.data.count;
};

export const deleteNotification = async (token: string, projectId: string, id: string): Promise<void> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/projects/${projectId}/notifications/${id}`, {
    method: 'DELETE',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to delete notification: ${response.status}`);
  }
};

// --- Unified (member-scoped) API functions ---

export const fetchUnifiedNotifications = async (
  token: string,
  params: NotificationListParams = {},
): Promise<NotificationListResponse> => {
  const searchParams = new URLSearchParams();
  if (params.page) searchParams.set('page', String(params.page));
  if (params.limit) searchParams.set('limit', String(params.limit));
  if (params.status) searchParams.set('status', params.status);
  if (params.type) searchParams.set('type', params.type);
  if (params.projectId) searchParams.set('projectId', params.projectId);

  const qs = searchParams.toString();
  const url = `${getApiUrl()}/api/notifications${qs ? `?${qs}` : ''}`;

  const response = await fetchWithAuthRetry(url, {
    cache: 'no-store',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch notifications: ${response.status}`);
  }

  return (await response.json()) as NotificationListResponse;
};

export const fetchUnifiedUnreadCount = async (token: string): Promise<number> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/notifications/unread-count`, {
    cache: 'no-store',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch unread count: ${response.status}`);
  }

  const data = (await response.json()) as UnreadCountResponse;
  return data.data.count;
};

export const fetchUnifiedNotification = async (token: string, id: string): Promise<NotificationItem> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/notifications/${id}`, {
    cache: 'no-store',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch notification: ${response.status}`);
  }

  const data = (await response.json()) as NotificationItemResponse;
  return data.data;
};

export const markUnifiedNotificationAsRead = async (token: string, id: string): Promise<NotificationItem> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/notifications/${id}`, {
    method: 'PATCH',
    headers: authHeaders(token, true),
    body: JSON.stringify({ status: 'READ' }),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to mark notification as read: ${response.status}`);
  }

  const data = (await response.json()) as NotificationItemResponse;
  return data.data;
};

export const markAllUnifiedNotificationsAsRead = async (token: string): Promise<number> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/notifications/read-all`, {
    method: 'PATCH',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to mark all as read: ${response.status}`);
  }

  const data = (await response.json()) as ReadAllResponse;
  return data.data.count;
};

export const markUnifiedNotificationsForTargetAsRead = async (token: string, entityId: string): Promise<number> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/notifications/read-target`, {
    method: 'PATCH',
    headers: authHeaders(token, true),
    body: JSON.stringify({ entityId }),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to mark target notifications as read: ${response.status}`);
  }

  const data = (await response.json()) as ReadTargetResponse;
  return data.data.count;
};

export const deleteUnifiedNotification = async (token: string, id: string): Promise<void> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/notifications/${id}`, {
    method: 'DELETE',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to delete notification: ${response.status}`);
  }
};
